import {
  Cancel,
  CheckCircle,
  Delete as DeleteIcon,
  Event,
  LocalHospital,
  MedicalInformation,
  Pending,
  Person,
  Search,
  ThumbUp,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import prescriptionService from "../services/prescriptionService";
import useAuthStore from "../store/authStore";

const ITEMS_PER_PAGE = 10;
const STATUT_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "en_attente", label: "En attente" },
  { value: "validée", label: "Validée" },
  { value: "refusée", label: "Refusée" },
  /* { value: "traitée", label: "Traitée" },
  { value: "terminée", label: "Terminée" },*/
];

const TYPE_OPTIONS = [
  { value: "all", label: "Tous les types" },
  { value: "pharma", label: "Pharmacie" },
  { value: "labo", label: "Laboratoire" },
];

const formatDate = (dateString) => {
  if (!dateString) return "Non spécifié";
  return format(new Date(dateString), "d MMMM yyyy 'à' HH:mm", { locale: fr });
};

const StatusChip = ({ statut }) => {
  const getStatusConfig = (statut) => {
    switch (statut) {
      case "en_attente":
        return { icon: <Pending />, label: "En attente", color: "warning" };
      case "validée":
        return { icon: <CheckCircle />, label: "Validée", color: "success" };
      case "refusée":
        return { icon: <Cancel />, label: "Refusée", color: "error" };
      /*  case 'traitée':
        return { icon: <CheckCircle />, label: 'Traitée', color: 'info' };
      case 'terminée':
        return { icon: <CheckCircle />, label: 'Terminée', color: 'success' };*/
      default:
        return { icon: <Pending />, label: statut, color: "default" };
    }
  };

  const config = getStatusConfig(statut);
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      variant="outlined"
      sx={{ ml: 1 }}
    />
  );
};

const PrescriptionCard = ({ prescription, onRefresh }) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [satisfactionModalOpen, setSatisfactionModalOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const isCliniqueAgent = [
    "agent_clinique",
    "admin_clinique",
    "medecin_clinique",
  ].includes(user?.role);
  const isPharmacyAgent = ["agent_pharmacie", "admin_pharmacie"].includes(
    user?.role
  );
  const isLaboAgent = ["agent_labo", "admin_labo"].includes(user?.role);

  const handleDelete = async () => {
    try {
      const response = await prescriptionService.deletePrescription(
        prescription.id
      );
      if (response.success) {
        toast.success("Prescription supprimée avec succès");
        setDeleteConfirmOpen(false);
        onRefresh();
      } else {
        toast.error(response.message || "Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Une erreur est survenue lors de la suppression");
    }
  };

  const handleSatisfaction = async (isSatisfied) => {
    try {
      const response = await prescriptionService.updatePrescriptionStatus(
        prescription.id,
        isSatisfied ? "terminée" : "refusée",
        isSatisfied ? "Prescription satisfaite" : "Prescription non satisfaite"
      );
      if (response.success) {
        toast.success(
          `Prescription ${isSatisfied ? "satisfaite" : "refusée"} avec succès`
        );
        setSatisfactionModalOpen(false);
        onRefresh();
      } else {
        toast.error(response.message || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Une erreur est survenue lors de la mise à jour");
    }
  };

  return (
    <Card sx={{ mb: 3, borderLeft: 4, borderColor: "primary.main" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MedicalInformation color="primary" />
            <Typography variant="h6" component="div">
              {prescription.titre}
            </Typography>
            <Chip
              label={
                prescription.type === "pharma" ? "Pharmacie" : "Laboratoire"
              }
              color="primary"
              size="small"
            />
          </Box>
          <StatusChip statut={prescription.statut} />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Person sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="body2">
                <strong>Patient:</strong> {prescription.assure.fullName}
                <Chip
                  label={`Matricule: ${prescription.matricule_assure}`}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Event sx={{ mr: 1, color: "info.main" }} />
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong>{" "}
                {formatDate(prescription.date_aprescription)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocalHospital sx={{ mr: 1, color: "secondary.main" }} />
              <Typography variant="body2">
                <strong>Médecin:</strong> Dr.{" "}
                {prescription.agentClinique?.full_name}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Description:</strong> {prescription.description}
            </Typography>
          </Grid>
        </Grid>

        {prescription.acte_medicals &&
          prescription.acte_medicals.length > 0 && (
            <Paper
              elevation={0}
              sx={{ p: 2, mb: 2, bgcolor: "background.default" }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Actes Médicaux:
              </Typography>
              <List dense>
                {prescription.acte_medicals.map((acte, index) => (
                  <React.Fragment key={acte.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2">
                              {acte.label} ({acte.code})
                            </Typography>
                            <Chip
                              label={`${acte.prescription_acte_medical.prix_total} FCFA`}
                              size="small"
                              color="success"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Quantité:{" "}
                              {acte.prescription_acte_medical.quantite}
                            </Typography>
                            {acte.prescription_acte_medical.posologie && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Posologie:{" "}
                                {acte.prescription_acte_medical.posologie}
                              </Typography>
                            )}
                            {acte.prescription_acte_medical
                              .duree_traitement && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Durée:{" "}
                                {
                                  acte.prescription_acte_medical
                                    .duree_traitement
                                }{" "}
                                jours
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {index < prescription.acte_medicals.length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}

        <Box
          sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          {isCliniqueAgent && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Supprimer
            </Button>
          )}

          {(isPharmacyAgent || isLaboAgent) &&
            prescription.statut === "validée" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ThumbUp />}
                onClick={() => setSatisfactionModalOpen(true)}
              >
                Marquer comme satisfaite
              </Button>
            )}
        </Box>

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <Typography>
              Êtes-vous sûr de vouloir supprimer cette prescription ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={satisfactionModalOpen}
          onClose={() => setSatisfactionModalOpen(false)}
        >
          <DialogTitle>Confirmer la satisfaction</DialogTitle>
          <DialogContent>
            <Typography>
              Voulez-vous marquer cette prescription comme satisfaite ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSatisfactionModalOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => handleSatisfaction(false)}
              color="error"
              variant="outlined"
            >
              Non satisfaite
            </Button>
            <Button
              onClick={() => handleSatisfaction(true)}
              color="success"
              variant="contained"
            >
              Satisfaite
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const PrescriptionsHistory = () => {
  const user = useAuthStore((state) => state.user);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [totalPages, setTotalPages] = useState(1);

  const defaultTypeFilter = useMemo(() => {
    if (["agent_pharmacie", "admin_pharmacie"].includes(user?.role)) {
      return "pharma";
    }
    if (["agent_labo", "admin_labo"].includes(user?.role)) {
      return "labo";
    }
    return "all";
  }, [user?.role]);

  useEffect(() => {
    setTypeFilter(defaultTypeFilter);
  }, [defaultTypeFilter]);

  const fetchPrescriptions = async (
    pageNumber,
    search = "",
    status = "all",
    type = "all"
  ) => {
    setLoading(true);
    try {
      const filters = {
        page: pageNumber,
        limit: ITEMS_PER_PAGE,
        search: search?.trim(),
        ...(status !== "all" && { statut: status }),
        ...(type !== "all" && { type }),
      };

      const response =
        await prescriptionService.getAllPrescriptionDetails(filters);
      if (response?.success) {
        setPrescriptions(response.data);
        setTotalPages(response.pagination?.totalPages || 1);
        setError(null);
      }
    } catch (err) {
      setError("Erreur lors du chargement des prescriptions");
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useMemo(() => debounce(fetchPrescriptions, 300), []);

  useEffect(() => {
    debouncedFetch(page, searchTerm, statusFilter, typeFilter);
    return () => debouncedFetch.cancel();
  }, [page, searchTerm, statusFilter, typeFilter]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <MedicalInformation
          sx={{ mr: 2, color: "primary.main", fontSize: 32 }}
        />
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          Historique des Prescriptions
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Rechercher par nom, matricule..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Statut"
            >
              {STATUT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {["agent_clinique", "admin_clinique", "medecin_clinique"].includes(
          user?.role
        ) && (
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                {TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : prescriptions.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          Aucune prescription trouvée
        </Alert>
      ) : (
        <>
          <Fade in={true}>
            <Box>
              {prescriptions.map((prescription) => (
                <PrescriptionCard
                  key={prescription.id}
                  prescription={prescription}
                  onRefresh={() =>
                    fetchPrescriptions(
                      page,
                      searchTerm,
                      statusFilter,
                      typeFilter
                    )
                  }
                />
              ))}
            </Box>
          </Fade>

          {totalPages > 1 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PrescriptionsHistory;
